import axios from "../utils/axios";
import { decryptData, saveData, postErrorSlack } from '../utils/index.jsx';
import { addBreadcrumb, flush, parseJwtSafe, jwtShape } from '../utils/diagnostics/breadcrumbs';
import { AUTH_PROBE } from './authProbe';
import * as jose from 'jose';
import CryptoJS from 'crypto-js';

// Shared failure reporter for fetchUser's two failure paths (the non-200 `else`
// branch and the `catch` branch). Both produce a byte-identical Slack report, so
// keep it in one place: decode the JWT for the user's email, attach the
// investigation UUID, and post to the auth/fetchUser channel.
function reportFetchUserFailure(errorReason, errorDetails) {
    let userEmail = "Unknown";
    let investigationUuid = localStorage.getItem("uuidInvestigation") || "Not available";

    if (localStorage.getItem("jwt")) {
        try {
            const payload = jose.decodeJwt(localStorage.getItem("jwt"));
            userEmail = payload.email || payload.uuid || "Unknown";
        } catch (e) {
            userEmail = "Unable to decode JWT";
        }
    }

    const slackMessage = `🔴 fetchUser Validation Failed\n\n` +
        `Reason: ${errorReason}\n` +
        `User Email: ${userEmail}\n` +
        `Investigation UUID: ${investigationUuid}\n` +
        `URL: ${window.location.href}\n` +
        `Details: ${JSON.stringify(errorDetails)}`;

    postErrorSlack("auth/fetchUser", slackMessage, errorDetails, null);
}

// JWT metadata for breadcrumbs. Guarded so a malformed/absent JWT can never break
// auth — on any failure we just omit the fields.
function jwtBreadcrumbFields() {
    try {
        const token = localStorage.getItem('jwt');
        const jwtInfo = parseJwtSafe(token);
        return {
            jwtExp: jwtInfo.jwtExp,
            jwtIat: jwtInfo.jwtIat,
            researcherUuid: jwtInfo.researcherUuid,
            // Shape works even when decode fails — captures the "jwt malformed" /
            // "jwt must be provided" cases that parseJwtSafe alone reports as {}.
            ...jwtShape(token),
        };
    } catch (_) {
        return {};
    }
}

// CHOM incident 2026-06-10 — fetchUser is a tri-state authentication probe:
//   'ok'           → JWT valid, keep the user authenticated
//   'unauthorized' → real 401 (expired/invalid JWT) → expel to login
//   'network'      → transient failure (no response, timeout, CORS-mirage, or a
//                    non-401 <500 status like a mod_evasive 403) → KEEP current
//                    auth; do NOT log out. The JWT stays in localStorage and the
//                    next real API call revalidates server-side.
// Only 'unauthorized' may log the user out. Previously this rejected on network
// errors, so a 150ms wifi blip expelled field users at CHOM.
export async function fetchUser(){
    try {
        const response = await axios.get(import.meta.env.VITE_APP_API_URL+'/researcher/validate', { headers: {"Authorization" : localStorage.getItem("jwt")}});
        if (response.status === 200) {
            return AUTH_PROBE.OK;
        } else {
            // Handle non-200 status codes (401, 403, etc.)
            // Since axios.validateStatus treats < 500 as success, we need to handle them here
            let errorReason = "Unknown error";

            const status = response.status;
            if (status === 401) {
                errorReason = "Unauthorized - Invalid or expired JWT token";
            } else if (status === 403) {
                errorReason = "Forbidden - User does not have permission";
            } else if (status >= 400) {
                errorReason = `Client Error (${status}) - Bad request`;
            } else {
                errorReason = `HTTP Error (${status}) - Unexpected status code`;
            }
            const errorDetails = {
                status: status,
                statusText: response.statusText,
                data: response.data,
                type: "http_error"
            };

            // Diagnostics: capture the exact 401/403 with JWT metadata so we can pair
            // with the server's auth.jwt.rejected / auth.permission.denied log.
            try {
                addBreadcrumb('state', 'auth.fetchUser.failed', {
                    status,
                    reason: errorReason.slice(0, 120),
                    url: '/researcher/validate',
                    ...jwtBreadcrumbFields(),
                });
                flush('auth-probe-failed');
            } catch (_) { /* never break auth */ }

            reportFetchUserFailure(errorReason, errorDetails);

            // Only a genuine 401 expels. Any other <500 status (e.g. a
            // mod_evasive 403 surfaced here by validateStatus) is treated
            // as transient so we never log a field user out over it.
            return status === 401 ? AUTH_PROBE.UNAUTHORIZED : AUTH_PROBE.NETWORK;
        }
    } catch (err) {
        console.error('fetchUser request failed', err?.response?.status ?? err?.message);

        // Determine the reason for the failure
        let errorReason = "Unknown error";
        let errorDetails = {};

        if (!err.response) {
            // No response at all (no err.response). The browser may surface this
            // as a "CORS error" in the console, but a *missing* response is NOT a
            // real CORS rejection — claiming "missing CORS headers" here was the
            // red herring that misdirected the CHOM incident (2026-06-10:
            // cors-errors.log was empty; the real cause was a proxy/mod_evasive
            // returning without CORS headers). Every no-response case is transient
            // and returns AUTH_PROBE.NETWORK regardless of sub-classification.
            if (err.code === "ETIMEDOUT" || err.message?.includes("timeout")) {
                errorReason = "Request timeout - no response in time";
            } else {
                errorReason = `No response (network/proxy/timeout) - ${err.message || err.code || 'unknown'}`;
            }
            errorDetails = {
                message: err.message,
                code: err.code,
                type: "network_error"
            };
        } else {
            // HTTP error - server responded with error status
            const status = err.response.status;
            if (status === 401) {
                errorReason = "Unauthorized - Invalid or expired JWT token";
            } else if (status === 403) {
                errorReason = "Forbidden - User does not have permission";
            } else if (status >= 500) {
                errorReason = `Server Error (${status}) - Internal server error`;
            } else if (status >= 400) {
                errorReason = `Client Error (${status}) - Bad request`;
            } else {
                errorReason = `HTTP Error (${status}) - Unexpected status code`;
            }
            errorDetails = {
                status: status,
                statusText: err.response.statusText,
                data: err.response.data,
                type: "http_error"
            };
        }

        try {
            addBreadcrumb('exception', 'auth.fetchUser.network', {
                url: '/researcher/validate',
                errorName: err?.name,
                errorMessage: err?.message,
                code: err?.code,
                status: err?.response?.status,
                ...jwtBreadcrumbFields(),
            });
            flush('auth-probe-failed');
        } catch (_) { /* never break auth */ }

        reportFetchUserFailure(errorReason, errorDetails);

        // Tri-state, never reject: a network error (no err.response),
        // timeout, or any non-401 error must NOT expel the user. Only a
        // real 401 surfaced via err.response is 'unauthorized'.
        return err.response?.status === 401 ? AUTH_PROBE.UNAUTHORIZED : AUTH_PROBE.NETWORK;
    }
}

// SHER-1147: older clients persisted the plaintext password in localStorage for the whole
// session. Remove what they left behind; called once at boot, so a session that was already
// open is cleaned without a logout. Only the password key is touched, and only when present:
// providerSherwood's Storage patch turns every auth-key removal into a breadcrumb plus a
// diagnostics flush, so an unconditional remove would log a fake event at every boot.
export function purgeStoredPassword() {
  try {
    if (localStorage.getItem("password") !== null) {
      localStorage.removeItem("password");
    }
  } catch (_) { /* localStorage may be unavailable; nothing to clean then */ }
}

export function signIn(credentials, typeUser) {
  return new Promise((resolve, reject) => {
    // SHER-1147: the plaintext password only lives in memory, long enough to unwrap
    // keyResearcher below. It is never written to storage.
    const password = credentials.password;
    credentials.password = CryptoJS.SHA256(credentials.password).toString(CryptoJS.enc.Base64)
    axios.post(import.meta.env.VITE_APP_API_URL+'/'+typeUser+'/login', credentials)
        .then((response) => {
            if(response.status === 200){
                saveData("jwt", response.data.jwt);
                saveData("type", typeUser);
                const payload = jose.decodeJwt(localStorage.getItem("jwt"));
                const rawKeyResearcher = decryptData(payload.keyResearcher, password);
                saveData("rawKeyResearcher", rawKeyResearcher);

                saveData("name", payload.name);
                saveData("surnames", payload.surnames);
                saveData("uuid", payload.uuid);
                // SHER-XYZ — when an admin has just reset this user's password, the JWT carries
                // mustChangePassword=true; the route guard reads this localStorage flag and
                // redirects to /auth/change-password until the user picks their own password.
                if (payload.mustChangePassword === true) {
                    saveData("forceChangePassword", "1");
                } else {
                    localStorage.removeItem("forceChangePassword");
                }
                axios.defaults.headers['Authorization'] = localStorage.getItem('jwt');
                resolve(response.data);
            }
            else{
                reject(response.data);
            }
        })
        .catch(err => {console.error('Sign-in request failed', err?.response?.status ?? err?.message); reject(err);});
  });
}

export function signUp(credentials) {
  return new Promise((resolve, reject) => {
    axios
      .post("/api/auth/sign-up", credentials)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        }
        reject(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function resetPassword(credentials) {
  return new Promise((resolve, reject) => {
    axios
      .post("/api/auth/reset-password", credentials)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        }
        reject(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}
